import React from 'react';
import { Box, IconButton, Typography } from '@mui/material';
import { Image as ImageIcon, Close as CloseIcon } from '@mui/icons-material';

interface ImageUploadFieldProps {
  value?: string;
  onChange?: (url: string) => void;
  onDelete?: () => void;
  onClick?: (e: React.MouseEvent<HTMLElement>) => void;
  onDrop?: (file: File) => void;
  label?: string;
  helperText?: React.ReactNode;
  placeholder?: string;
  height?: number | string;
  alt?: string;
  disabled?: boolean;
}

export const ImageUploadField: React.FC<ImageUploadFieldProps> = ({
  value,
  onChange,
  onDelete,
  onClick,
  onDrop,
  label,
  helperText,
  placeholder = 'クリックして画像を選択',
  height = 120,
  alt = '画像',
  disabled = false,
}) => {
  const handleDelete = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (onDelete) {
      onDelete();
    } else if (onChange) {
      onChange('');
    }
  };

  const handleDragOver = (e: React.DragEvent) => {
    if (disabled || !onDrop) return;
    e.preventDefault();
    e.dataTransfer.dropEffect = 'copy';
  };

  const handleDrop = (e: React.DragEvent) => {
    if (disabled || !onDrop) return;
    e.preventDefault();
    const files = Array.from(e.dataTransfer.files);
    const imageFile = files.find(file => file.type.startsWith('image/'));
    if (imageFile) {
      onDrop(imageFile);
    }
  };

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
      {label && (
        <Box>
          <Typography variant="subtitle1" sx={{ fontWeight: 600, fontSize: '16px' }}>
            {label}
          </Typography>
          {helperText && (
            <Typography variant="caption" sx={{ display: 'block', color: '#999999', fontSize: '10px' }}>
              {helperText}
            </Typography>
          )}
        </Box>
      )}
      <Box
        sx={{
          border: '1px solid',
          borderColor: '#D0D0D0',
          borderRadius: '16px',
          p: 2,
          textAlign: 'center',
          cursor: disabled ? 'default' : 'pointer',
          position: 'relative',
          height: height,
          backgroundColor: '#FFFFFF',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          overflow: 'hidden',
        }}
        onClick={disabled ? undefined : onClick}
        onDragOver={handleDragOver}
        onDrop={handleDrop}
      >
        {value ? (
          <Box
            sx={{
              position: 'relative',
              width: '100%',
              height: '100%',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              backgroundColor: '#F5F5F5',
              borderRadius: '12px',
              overflow: 'hidden',
            }}
          >
            <img
              src={value}
              alt={alt}
              style={{
                maxWidth: '100%',
                maxHeight: '100%',
                width: 'auto',
                height: 'auto',
                objectFit: 'contain',
              }}
            />
            {!disabled && (
              <IconButton
                size="small"
                sx={{
                  position: 'absolute',
                  top: 4,
                  right: 4,
                  backgroundColor: 'white',
                  boxShadow: '0px 2px 4px rgba(0,0,0,0.2)',
                  width: 20,
                  height: 20,
                  minWidth: 20,
                  padding: 0,
                  '&:hover': {
                    backgroundColor: '#f5f5f5',
                  },
                }}
                onClick={handleDelete}
              >
                <CloseIcon sx={{ fontSize: 14, color: '#999999' }} />
              </IconButton>
            )}
          </Box>
        ) : (
          <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 0.5 }}>
            <ImageIcon sx={{ fontSize: 32, color: '#999999' }} />
            <Typography variant="body2" sx={{ color: '#999999', fontSize: '11px' }}>
              {placeholder}
            </Typography>
          </Box>
        )}
      </Box>
    </Box>
  );
};

